<template>
	<div id="holder">
		<iframe v-if="srcDoc != ''" id="ar-iframe" :srcdoc="srcDoc"></iframe>
		<iframe v-if="srcUrl != ''" id="ar-iframe" :src="srcUrl"
			allow="fullscreen; camera; microphone; gyroscope; accelerometer; xr-spatial-tracking"
			allowfullscreen></iframe>
		<ModalComponent :show="showClaimModal" @close="showClaimModal = false" @claim="claimItem()" />
		<LoadingComponent v-if="loading" />
	</div>
</template>

<script lang="ts">
declare global {
	interface Window {
		claimedIds?: Set<string>;
	}
}

import ModalComponent from "@/components/utils/ModalComponent.vue";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { getUserLocation } from "@/utils/geolocationTools";
import { Action, ActionType, Container } from "@/types/customPage";
import { ARTemplate } from "@/types/arTemplate";
import { DropLocation, DropPool } from "@/types/dropPool";
import { defineComponent, PropType } from "vue";
import { navigationStore } from "@/store/pinia_modules/navigationStore";
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore";
import { experienceStore } from "@/store/pinia_modules/experienceStore";
import { vItemStore } from "@/store/pinia_modules/vItemStore";
import { ItemStatus } from "@/types/vItem";
import { authStore } from "@/store/pinia_modules/authStore";
import { poolSegmentStore } from "@/store/pinia_modules/poolSegmentStore";
import { campaignStore } from "@/store/pinia_modules/campaignStore";
import { getRouter } from "@/utils/routerRef";
import { deepLinkStore } from "@/store/pinia_modules/deepLinkStore";

export default defineComponent({
	props: {
		element: {
			type: Object as PropType<Container>,
			required: true,
		},
	},
	components: { ModalComponent, LoadingComponent },
	data() {
		return {
			showClaimModal: false,
			selectedItemID: "",
			srcDoc: "",
			srcUrl: "",
			template: {} as ARTemplate,
			iframeElement: null as HTMLIFrameElement | null,
			loading: false,
			reachedAccuracy: false,
			initNoItems: false,
			rendered: false,
			templateHasAnimations: false,
			iframeMessageHandler: null as ((event: MessageEvent) => void) | null,
		};
	},

	async mounted() {
		this.loading = true;

		// Hydrate vItems from the backend instead of clearing the store.
		// Previously this called vItemStore().clearItems() and relied on a
		// later dropItems() POST to refill the store. That broke the map→AR
		// handoff: the allocator's dedup window (USER_ALREADY_DROPPED) plus
		// the 10-min cleanup threshold (vitem.repo cron) meant the redrop
		// often returned silently and the AR rendered blank until the user
		// went back to map and re-entered. getItems() (GET) reads the wallet's
		// current vitems straight from DDB, so existing DROPPED items the map
		// already saw are preserved. The vItems computed below (getDroppedItems
		// / getPendingItems) filters CLAIMED/REDEEMED out, so the iframe is
		// still protected from receiving already-claimed data. If the wallet
		// genuinely has no items, the else branch below falls through to
		// init()→dropItems() and the allocator runs as before.
		await vItemStore().getItems();

		await this.getPoolsCategoriesPrizes();
		experienceStore().resolveFromState({
			routeName: "AR",
			pools: dropPoolStore().getPools,
			pendingItems: vItemStore().getPendingItems,
		});

		// Refresh the active pool with the fresh data
		dropPoolStore().saveActiveDropPool({
			poolID: experienceStore().preferredPoolID,
			dropType: experienceStore().preferredDropType,
			fallbackDropType:
				experienceStore().preferredDropType === "gps" ? "pin" : "gps",
		});

		const id = dropPoolStore().getWatcher;
		if (id != -1) {
			navigator.geolocation.clearWatch(id);
			dropPoolStore().saveWatcher(-1);
		}
		navigationStore().isInARView();
		this.setupIframeListener();
		const shouldRequestLocation = await this.shouldRequestLocationPermission();
		if (shouldRequestLocation) {
			dropPoolStore().saveLocationAccuracy(1000000000);
			this.initUserLocation();
		} else if (this.vItems.length > 0) {
			await this.checkForEnhancedAI();
		} else {
			this.reachedAccuracy = true;
			await this.init();
		}
	},
	beforeUnmount() {
		console.log("ARComponent beforeUnmount");
		if (this.iframeMessageHandler) {
			window.removeEventListener("message", this.iframeMessageHandler);
			this.iframeMessageHandler = null;
		}
	},
	computed: {
		vItems() {
			if (vItemStore().getPendingItems.length > 0) {
				return vItemStore().getPendingItems;
			}
			return vItemStore().getDroppedItems;
		},
		action(): Action {
			return navigationStore().getAction;
		},
		activePool(): DropPool | null {
			return dropPoolStore().getActivePool;
		},
		activeDropLocation(): DropLocation | null {
			return dropPoolStore().getActiveDropLocation;
		},
		loading(): boolean {
			return dropPoolStore().getLoading;
		},
		walletId(): string | null {
			return authStore().getWalletID;
		},
		userLocation(): [number, number] {
			return dropPoolStore().getUserLocation;
		},
		locationAccuracy(): number {
			return dropPoolStore().getLocationAccuracy;
		},
		requiresGpsLocation(): boolean {
			return experienceStore().requiresGpsLocation;
		},
	},
	watch: {
		action(to: Action): void {
			if (to) {
				if (to.type == "claim-item") {
					this.claimItem();
				}
			}
		},
		locationAccuracy(to: number) {
			if (to && to <= 100 && !this.reachedAccuracy) {
				// if (to && to <= 1000000 && !this.reachedAccuracy) {
				this.reachedAccuracy = true;
				this.init();
			}
		},
		userLocation(to: [number, number]) {
			if (to && this.requiresGpsLocation) {
				if (vItemStore().getPendingItems.length == 0) {
					dropPoolStore().saveActiveDropPool({
						poolID: experienceStore().preferredPoolID,
						dropType: experienceStore().preferredDropType,
						fallbackDropType: "pin",
					});
				}
			}
		},
	},
	methods: {
			async checkForEnhancedAI() {
				if (this.initNoItems && this.vItems.length == 0) {
					this.loading = false;
					return;
				}

				if (this.vItems.length > 0 && !this.rendered) {
					//drop items here if bypass map
					this.loading = true;
					const walletId = authStore().getWalletID;
					this.rendered = true;
					if (this.activePool?.arTemplateId) {
						this.template = await dropPoolStore().getARTemplate(
							this.activePool.arTemplateId
						);
						if (this.template.framework == "unity") {
							this.srcUrl = this.template.srcUrl! + "/template.html";
							this.loading = false;
							return;
						}
					} else {
						const campaign = campaignStore().getItem;
						if (campaign?.pendingarTemplateId) {
							this.template = await dropPoolStore().getARTemplate(
								campaign?.pendingarTemplateId || ""
							);
						} else {
							console.error("No template found to render AR");
							this.loading = false;
							getRouter().push(`/${campaign?.id}/vault`);
							return;
						}
					}
					const campaign = campaignStore().getItem;

					this.srcDoc = await dropPoolStore().renderARTemplate({
						walletId: walletId || "",
						arTemplateID:
							(this.activePool?.useEnhancedAI
								? this.activePool.arTemplateId
								: campaign?.pendingarTemplateId || "legacy") || "legacy",
						dropPoolId: this.activePool?.id || "",
						deepLinkId: deepLinkStore().deepLink?.id || "",
						userLocation: this.requiresGpsLocation ? this.userLocation : undefined,
					});
					this.loading = false;
				} else if (this.vItems.length == 0) {
					//get user location
					this.loading = true;
					this.initNoItems = true;
					if (await this.shouldRequestLocationPermission()) {
						this.initUserLocation();
					} else {
						this.loading = false;
					}
				}
			},
			async init() {
				// if pending item, don't drop items
				if (vItemStore().getPendingItems.length == 0) {
					vItemStore().clearItems();
					dropPoolStore().saveActiveDropPool({
						poolID: experienceStore().preferredPoolID,
						dropType: experienceStore().preferredDropType,
						fallbackDropType: "pin",
					});
					await this.dropItems();
				}
				this.checkForEnhancedAI();
			},
			initUserLocation() {
				getUserLocation();
			},
			async shouldRequestLocationPermission(): Promise<boolean> {
				return this.requiresGpsLocation;
			},
			setGetLocationTimeout() {
				setTimeout(() => {
					if (!this.reachedAccuracy) {
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "location-error",
						});
						this.loading = false;
					}
				}, 10000);
			},
			async getPoolsCategoriesPrizes() {
				await dropPoolStore().getMapData();
			},
			async getCampaignSegments() {
				poolSegmentStore().getCampaignSegments();
			},
		async dropItems() {
			// if pending item, don't drop items
			if (vItemStore().getPendingItems.length > 0) {
				return false;
			}

			// Ensure we have an active pool to check against.
			// This determines if there's a campaign running at the user's location and time.
			if (!this.activePool) {
				dropPoolStore().saveActiveDropPool({
					poolID: experienceStore().preferredPoolID,
					dropType: experienceStore().preferredDropType,
					fallbackDropType: "pin",
				});
			}

			// If still no active pool, user is not in a valid area for drops at this time.
			if (!this.activePool) {
				navigationStore().emitAction({
					type: ActionType.OPEN_POPUP,
					value: "out-of-drop-zone",
				});
				return false;
			}

			// Step 1: Pre-Drop Authorization with zoneCheck.
			// This verifies the user is inside a specific geofenced area (polygon or radius).
			if (this.activePool.dropType === "gps") {
				const lat = this.userLocation[1];
				const lng = this.userLocation[0];
				const zoneCheckResponse = await dropPoolStore().zoneCheck({
					poolID: this.activePool.id!,
					latitude: lat,
					longitude: lng,
					force: true,
				});

				if (!zoneCheckResponse?.data.allowed) {
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "out-of-drop-zone",
					});
					return false;
				}
			}

			// Step 2: Initiate the Drop with dropItems, but only if no items are currently loaded.
			if (this.vItems.length == 0) {
				// await vItemStore().recycleItems({
				// 	walletId: this.walletId!,
				// 	poolID: this.activePool.id!,
				// });

					await vItemStore().dropItems({
						walletId: authStore().getWalletID || "",
						segmentId: null,
						poolId: this.activePool.id!,
						userLocation: this.requiresGpsLocation
							? [this.userLocation[1], this.userLocation[0]]
							: undefined,
					});
				}
			return true;
		},

		selectItem(id: string) {
			console.log("Item ID /////////////////////////");
			console.log(id);
			this.selectedItemID = id;


			if (this.template.framework) {
				if (this.template.framework == "unity") {
					this.claimItem();
				} else {
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "claim-item",
					});
				}
			} else {
				navigationStore().emitAction({
					type: ActionType.OPEN_POPUP,
					value: "claim-item",
				});
			}
		},
		async claimItem() {
			let id = "";
			if (this.selectedItemID.includes("webar-marker-")) {
				id = this.selectedItemID.split("/")[1];
			} else {
				id = this.selectedItemID;
			}
			// check if item is pending
			const pendingItem = vItemStore().getPendingItems.find(
				(item) => item.id == id
			);
			try {
				await vItemStore().claimItem(id);
				this.sendMessage("itemClaimed", id);

				// check selected vitem skipToGameplay
				const vitem = vItemStore().items.find(
					(item) => item.id == id
				);

				this.showClaimModal = false;
				this.loading = false;
				if (pendingItem && pendingItem.isDeepLink) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				// set as selected item if skipToGameplay is true
				// this will be used in the vault to skip to the prize page rendering
				if (vitem && vitem.skipToGameplay) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
			} catch (error) {
				console.error(error);
				this.showClaimModal = false;
			}
		},
		sendMessage(event: string, message: any) {
			this.iframeElement = document.getElementById(
				"ar-iframe"
			) as HTMLIFrameElement;
			const msg = JSON.stringify({
				event: event,
				message: message,
			});
			this.iframeElement!.contentWindow!.postMessage(msg, "*");
		},
		setupIframeListener() {
			this.iframeElement = document.getElementById(
				"ar-iframe"
			) as HTMLIFrameElement;

			this.iframeMessageHandler = async (e: MessageEvent) => {
				const data = JSON.parse(e.data);
				switch (data.event) {
					case "getAuth":
						const lastGps = authStore().lastKnownGps;
						const authObject = {
							campaignId: authStore().getCampaignID,
							userId: authStore().getUserID,
							walletId: authStore().getWalletID,
							refreshToken: localStorage.getItem("RefreshToken"),
							accessToken: localStorage.getItem("AccessToken"),
							billingSessionId: authStore().getSessionID,
							journeyId: authStore().getJourneyID,
							// Forward the user-app's most recent GPS sample so the
							// proxy/standard-quiz can include it in experience.*
							// POST bodies. IRB's getRequestGeoContext picks it up
							// from body.gps and threads it through to the
							// experience.ended EventBridge Detail — which the
							// CC analytics historical-query handler uses for
							// per-city gameplay rollup (see
							// ir-signup-poc/backend/src/handlers/query-historical-analytics.ts:539).
							gps: lastGps
								? { lat: lastGps.lat, lon: lastGps.lon, accuracy: lastGps.accuracy, source: "gps" }
								: null,
						};
						this.sendMessage("auth", authObject);
						break;
					case "getDroppedItems":
						// If the store is empty (cleared on mount or after claim),
						// re-fetch from the backend so we never send stale data.
						if (this.vItems.length === 0 && this.activePool?.id) {
							await vItemStore().getItems();
						}
						this.sendMessage("droppedItems", this.vItems);
						break;
					case "claimItem":
						try {
							// const item = JSON.parse(data.message);
							this.selectItem(data.message);
						} catch (err) {
							console.error(err);
						}
						break;
					case "claim-item-directly":
						if (!window.claimedIds) window.claimedIds = new Set();
						if (window.claimedIds.has(data.message)) {
							return;
						}
						try {
							window.claimedIds.add(data.message);
							this.selectedItemID = data.message;
							await this.claimItem();

							// Remove claimed items from the store so that if
							// the iframe reloads (iOS back-nav) it cannot
							// receive the same items again.
							const remaining = vItemStore().items.filter(
								(item) => item.itemStatus === ItemStatus.DROPPED
							);
							vItemStore().items = remaining;
						} catch (err) {
							console.error(err);
						}
						break;
					case "alreadyClaimed":
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "already-claimed",
						});
						break;
					case "templateInit":
						// Initialize template configuration
						console.log("AR template initialized with config:", data.message);
						if (data.message && typeof data.message.hasAnimations === 'boolean') {
							this.templateHasAnimations = data.message.hasAnimations;
						}
						break;
					case "emitAction":
						navigationStore().emitAction(data.message);
						break;
				}
			};
			window.addEventListener("message", this.iframeMessageHandler);
		},
	},
});
</script>

<style scoped lang="scss">
#holder {
	width: 100%;
	height: 100%;
	background-color: #000;
}

#ar-iframe {
	width: 100%;
	height: 100%;
	border-style: none;
	background-color: #000;
}

a-scene {
	width: 100%;
	height: 100%;
}

.ar-loading {
	position: fixed;
	width: 100%;
	height: 100%;
	background-color: #000;
	z-index: 100;
}

.cross-hair {
	position: absolute;
	top: 50%;
	left: 50%;

	.hair {
		width: 200px;
		border-style: solid;
		border-width: 1px 0 0 0;
		border-color: #fff;
		position: absolute;
		transform: translate(-50%, -50%);
		opacity: 0.2;
	}

	.vertical {
		transform: rotate(90deg);
		left: -100px;
	}
}
</style>
